import { LandingAppBar } from '../../components/LandingAppBar';
import Footer from '../../components/Footer';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { CONTACT_US_SUBMISSION_COLLECTION } from '../../db';

const schema = yup.object().shape({
  firstName: yup.string().max(250).required().label('First name'),
  lastName: yup.string().max(250).required().label('Last name'),
  company: yup.string().max(500).required().label('Company'),
  email: yup.string().email().required().label('Email'),
  phoneNumber: yup.string().max(15).required().label('Phone number'),
  message: yup.string().max(512).required().label('Message'),
});

export interface ContactUSForm {
  id?: string;
  firstName: string;
  lastName: string;
  company: string;
  email: string;
  phoneNumber: string;
  message: string;
}

export default function ContactUS() {
  const { register, handleSubmit, reset } = useForm({
    defaultValues: {
      firstName: '',
      lastName: '',
      company: '',
      email: '',
      phoneNumber: '',
      message: '',
    },
    resolver: yupResolver(schema),
  });

  function onSubmit(data: ContactUSForm) {
    CONTACT_US_SUBMISSION_COLLECTION.add(data)
      .then(() => {
        alert('Submitted successfully');
        reset();
      })
      .catch((e) => {
        alert('Something went wrong');
      });
  }

  return (
    <>
      <LandingAppBar />
      <div className="pt-10 bg-gradient-to-r from-blue-900 to-blue-800 sm:pt-16 lg:pt-8 lg:pb-14 lg:overflow-hidden">
        <div className="mx-auto max-w-7xl lg:px-8">
          <div className="lg:grid lg:grid-cols-2 lg:gap-8">
            <div className="max-w-md px-4 sm:max-w-2xl sm:px-6 sm:text-center lg:px-0 lg:text-left lg:flex">
              <div className="lg:py-24">
                <h1 className="mt-4 text-4xl tracking-tight font-extrabold text-white sm:mt-5 sm:text-6xl lg:mt-6 xl:text-6xl">
                  <span className="block">Contact Us</span>
                </h1>
              </div>
            </div>
            <div className="lg:mt-12 -mb-16 sm:-mb-48 lg:m-0 lg:relative pb-28 mt-4">
              <div className="mx-auto max-w-md px-4 sm:max-w-2xl sm:px-6 lg:max-w-none lg:px-0 min-h-full">
                <h1 className="w-full lg:absolute lg:inset-y-0 lg:left-0 lg:h-full lg:w-auto lg:max-w-none text-white text-2xl tracking-tight text-white lg:pt-16 pt-0 text-3xl qheading">
                  “Strive not to be a success, but rather to be of value.”{' '}
                  <br /> – Albert Einstein
                </h1>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-white py-16 px-4 overflow-hidden sm:px-6 lg:px-8 lg:py-24">
        <div className="relative max-w-xl mx-auto">
          <svg
            className="absolute left-full transform translate-x-1/2"
            width={404}
            height={404}
            fill="none"
            viewBox="0 0 404 404"
            aria-hidden="true"
          >
            <defs>
              <pattern
                id="85737c0e-0916-41d7-917f-596dc7edfa27"
                x={0}
                y={0}
                width={20}
                height={20}
                patternUnits="userSpaceOnUse"
              >
                <rect
                  x={0}
                  y={0}
                  width={4}
                  height={4}
                  className="text-gray-200"
                  fill="currentColor"
                />
              </pattern>
            </defs>
            <rect
              width={404}
              height={404}
              fill="url(#85737c0e-0916-41d7-917f-596dc7edfa27)"
            />
          </svg>
          <svg
            className="absolute right-full bottom-0 transform -translate-x-1/2"
            width={404}
            height={404}
            fill="none"
            viewBox="0 0 404 404"
            aria-hidden="true"
          >
            <defs>
              <pattern
                id="85737c0e-0916-41d7-917f-596dc7edfa27"
                x={0}
                y={0}
                width={20}
                height={20}
                patternUnits="userSpaceOnUse"
              >
                <rect
                  x={0}
                  y={0}
                  width={4}
                  height={4}
                  className="text-gray-200"
                  fill="currentColor"
                />
              </pattern>
            </defs>
            <rect
              width={404}
              height={404}
              fill="url(#85737c0e-0916-41d7-917f-596dc7edfa27)"
            />
          </svg>
          <div className="text-center">
            <p className="mt-4 text-base leading-6 text-gray-700 font-bold">
              V REKRUT values your business and We can make hiring easy for your
              organization. Please write us at{' '}
              <a href="mailto:vrekrut@vrekrut.com" className="text-blue-800">
                vrekrut@vrekrut.com
              </a>{' '}
            </p>
            <p className="mt-2 text-base leading-6 text-gray-700 font-bold">
              Aspiring to be a part of the V REKRUT team, Please drop us an
              email at{' '}
              <a href="mailto:hr@vrekrut.com" className="text-blue-800">
                hr@vrekrut.com
              </a>
            </p>
          </div>
          <div className="mt-12">
            <form
              action="#"
              onSubmit={handleSubmit(onSubmit)}
              method="POST"
              className="grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-8"
            >
              <div>
                <label
                  htmlFor="first_name"
                  className="block text-sm font-medium text-gray-700"
                >
                  First name
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    {...register('firstName')}
                    id="first_name"
                    autoComplete="given-name"
                    className="py-3 px-4 block w-full shadow-sm focus:ring-blue-500 focus:border-blue-500 border-gray-300 rounded-md"
                  />
                </div>
              </div>
              <div>
                <label
                  htmlFor="last_name"
                  className="block text-sm font-medium text-gray-700"
                >
                  Last name
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    {...register('lastName')}
                    id="last_name"
                    autoComplete="family-name"
                    className="py-3 px-4 block w-full shadow-sm focus:ring-blue-500 focus:border-blue-500 border-gray-300 rounded-md"
                  />
                </div>
              </div>
              <div className="sm:col-span-2">
                <label
                  htmlFor="company"
                  className="block text-sm font-medium text-gray-700"
                >
                  Company
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    {...register('company')}
                    id="company"
                    autoComplete="organization"
                    className="py-3 px-4 block w-full shadow-sm focus:ring-blue-500 focus:border-blue-500 border-gray-300 rounded-md"
                  />
                </div>
              </div>
              <div className="sm:col-span-2">
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700"
                >
                  Email
                </label>
                <div className="mt-1">
                  <input
                    id="email"
                    {...register('email')}
                    type="email"
                    autoComplete="email"
                    className="py-3 px-4 block w-full shadow-sm focus:ring-blue-500 focus:border-blue-500 border-gray-300 rounded-md"
                  />
                </div>
              </div>
              <div className="sm:col-span-2">
                <label
                  htmlFor="phone_number"
                  className="block text-sm font-medium text-gray-700"
                >
                  Phone number with country code
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <input
                    type="text"
                    {...register('phoneNumber')}
                    id="phone_number"
                    autoComplete="tel"
                    className="py-3 px-4 block w-full focus:ring-blue-500 focus:border-blue-500 border-gray-300 rounded-md"
                  />
                </div>
              </div>
              <div className="sm:col-span-2">
                <label
                  htmlFor="message"
                  className="block text-sm font-medium text-gray-700"
                >
                  Message
                </label>
                <div className="mt-1">
                  <textarea
                    id="message"
                    {...register('message')}
                    rows={4}
                    className="py-3 px-4 block w-full shadow-sm focus:ring-blue-500 focus:border-blue-500 border border-gray-300 rounded-md"
                    defaultValue={''}
                  />
                </div>
              </div>
              <div className="sm:col-span-2">
                <button
                  type="submit"
                  className="w-full inline-flex items-center justify-center px-6 py-3 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-blue-900 hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Let's talk
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <p className="mt-4 text-base leading-6 text-gray-700 text-center">
        Sharjah Media City (Shams) Sharjah, <br /> United Arab Emirates. <br />
        P.O. Box: 515000 <br /> <br /> Dial Us: {'  '} <br /> <br />
        <a href="tel:+971585528499" className="cursor-pointer">
          +971 58 552 8499
        </a>
        <br />
        <a className="cursor-pointer" href="tel:+971585452848">
          +971 58 545 2848
        </a>{' '}
        <br /> <a href="tel:+971585939533">+971 58 593 9533</a>
      </p>
      <Footer />
    </>
  );
}
